"use client";

import LinkExt from "@tiptap/extension-link";
import { Mark, mergeAttributes, type JSONContent } from "@tiptap/core";

export const EMPTY_DOC: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }],
};

export function normalizeBasePath(path?: string) {
  const raw = (path ?? "").trim();
  if (!raw) return "/content/";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withSlash.endsWith("/") ? withSlash : `${withSlash}/`;
}

export const ContentLink = Mark.create({
  name: "contentLink",
  priority: 1000,
  inclusive: false,
  excludes: "link",

  addOptions() {
    return {
      basePath: "/content/",
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      contentId: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-content-id"),
        renderHTML: (attrs) =>
          attrs?.contentId ? { "data-content-id": attrs.contentId } : {},
      },
      contentSlug: {
        default: null,
        parseHTML: (el) =>
          (el as HTMLElement).getAttribute("data-content-slug"),
        renderHTML: (attrs) =>
          attrs?.contentSlug ? { "data-content-slug": attrs.contentSlug } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "a[data-content-slug]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const slug =
      (HTMLAttributes as Record<string, unknown>)["data-content-slug"] ??
      (HTMLAttributes as Record<string, unknown>).contentSlug;
    const contentId =
      (HTMLAttributes as Record<string, unknown>)["data-content-id"] ??
      (HTMLAttributes as Record<string, unknown>).contentId;
    const basePath = normalizeBasePath((this.options as { basePath?: string }).basePath);

    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        title: typeof slug === "string" ? `${basePath}${slug}` : "",
        class: [
          "content-link cursor-pointer bg-[radial-gradient(circle,#d1d5db_55%,transparent_56%)] bg-[length:4px_2px] bg-[position:0_calc(100%-1px)] bg-repeat-x text-gray-900",
          (HTMLAttributes as Record<string, string | undefined>).class ?? "",
        ]
          .filter(Boolean)
          .join(" "),
        "data-content-id": contentId || undefined,
      }),
      0,
    ];
  },
});

export const PinkLink = LinkExt.extend({
  parseHTML() {
    return [{ tag: "a[href]:not([data-content-slug])" }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        title: HTMLAttributes.href ?? "",
        class: [
          "text-pinkCustom underline underline-offset-2 hover:opacity-80",
          HTMLAttributes.class ?? "",
        ]
          .filter(Boolean)
          .join(" "),
      }),
      0,
    ];
  },
});

export function parseEditorValue(value?: string | JSONContent) {
  const next = value ?? EMPTY_DOC;

  if (typeof next === "object" && next?.type === "doc") {
    return { kind: "json" as const, content: next };
  }

  if (typeof next === "string") {
    const trimmed = next.trim();

    if (!trimmed) {
      return { kind: "json" as const, content: EMPTY_DOC };
    }

    if (trimmed[0] === "{") {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed?.type === "doc") {
          return { kind: "json" as const, content: parsed as JSONContent };
        }
      } catch {
        // Fall through to HTML.
      }
    }

    return { kind: "html" as const, content: next };
  }

  return { kind: "json" as const, content: EMPTY_DOC };
}
